import { Amplify } from "aws-amplify";
import * as Auth from "aws-amplify/auth";
import { defaultStorage } from 'aws-amplify/utils';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import React, { createContext, useContext, useEffect, useState } from "react";
import { AwsConfigAuth } from "../config/auth";
import { AmplifyConfig } from "../types/AmplifyConfig";
import { clearCognitoStorage } from "../lib/storage";

interface UseAuth {
    isConfigured: boolean;
    isSignedIn: boolean;
    needMFA: boolean;
    configureAmplify: (config: AmplifyConfig) => void;
    signIn: (email: string, password: string, config: AmplifyConfig) => Promise<Result>;
    sendMfaCode: (code: string, config: AmplifyConfig) => Promise<Result>;
    signOut: (config: AmplifyConfig, clear?: boolean) => Promise<Result>;
    federatedSignIn: (identityProvider: string, config: AmplifyConfig) => Promise<Result>;
    forgotPassword: (email: string, config: AmplifyConfig) => Promise<Result>;
    forgotPasswordSubmit: (email: string, code: string, password: string, config: AmplifyConfig) => Promise<Result>;
    confirmVerificationCode: (email: string, code: string, config: AmplifyConfig) => Promise<Result>;
    fetchAuthSession: (config: AmplifyConfig) => Promise<Auth.AuthSession>;
}

interface Result {
    success: boolean;
    message: string;
}

type Props = {
    children?: React.ReactNode;
};

const authContext = createContext({} as UseAuth);

export const ProvideAuth: React.FC<Props> = ({ children }) => {
    const auth = useProvideAuth();
    return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

export const useAuth = () => {
    return useContext(authContext);
};

const parseErrorMessage = (error: unknown): string => {
    let message = "";
    if (error instanceof Error) {
        message = error.message;
    } else if (typeof error === "string") {
        message = error;
    } else {
        message = JSON.stringify(error);
    }

    if (message.startsWith("Invalid session for the user")) {
        // TODO: use i18n
        message = "Verification code expired. Press Back and try again.";
    }

    return message;
}

const useProvideAuth = (): UseAuth => {
    const [isConfigured, setIsConfigured] = useState(false);
    const [isSignedIn, setIsSigninedIn] = useState(false);
    const [needMFA, setNeedMFA] = useState(false);

    cognitoUserPoolsTokenProvider.setKeyValueStorage(defaultStorage);

    useEffect(() => {
        if (!isConfigured) {
            setIsSigninedIn(false);
            setNeedMFA(false);
            return;
        }

        Auth.getCurrentUser()
            .then(({ username, userId, signInDetails}) => {
                setIsSigninedIn(true);
                setNeedMFA(false);
            })
            .catch(() => {
                setIsSigninedIn(false);
                setNeedMFA(false);
            });
    }, [isConfigured]);

    const configureAmplify = (config: AmplifyConfig) => {
        const authConfig = AwsConfigAuth(config.region, config[config.region]);
        
        try {
            Amplify.configure(authConfig);
            setIsConfigured(true);
        } catch (error) {
            setIsConfigured(false);
        }
    }

    const signIn = async (email: string, password: string, config: AmplifyConfig) => {
        try {
            configureAmplify(config);
            const { isSignedIn, nextStep } = await Auth.signIn(
                {
                    username: email.toLowerCase(), 
                    password,
                    options: { authFlowType: 'CUSTOM_WITH_SRP' }
                },
            );
            setNeedMFA(nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE');
            setIsSigninedIn(isSignedIn);
            return { success: true, message: "" };
        } catch (error) {
            console.error(`[signIn(${email})]`, parseErrorMessage(error), JSON.stringify(error));
            return {
                success: false,
                message: parseErrorMessage(error),
            };
        }
    };

    const sendMfaCode = async (code: string, config: AmplifyConfig) => {
        try {
            configureAmplify(config);
            const output = await Auth.confirmSignIn({ challengeResponse: code });

            if (!output?.isSignedIn) {
                return {
                    success: false,
                    message: "Wrong code. Try again.",
                };
            }

            setNeedMFA(false);
            setIsSigninedIn(output?.isSignedIn);
            return { success: true, message: "" };
        } catch (error) {
            console.error("[sendMfaCode]", parseErrorMessage(error), JSON.stringify(error));
            return {
                success: false,
                message: parseErrorMessage(error),
            };
        }
    };

    const signOut = async (config: AmplifyConfig) => {
        try {
            // NOTE: signOut() redirects to sign-in page after sign-out
            // configureAmplify(config);
            // await Auth.signOut();

            clearCognitoStorage();
            setNeedMFA(false);
            setIsSigninedIn(false);
            return { success: true, message: "" };
        } catch (error) {
            console.error("[signOut]", parseErrorMessage(error), JSON.stringify(error));
            return {
                success: false,
                message: parseErrorMessage(error),
            };
        }
    };

    const federatedSignIn = async (identityProvider: string, config: AmplifyConfig) => {
        try {
            configureAmplify(config);
            await Auth.signInWithRedirect({provider: {custom: identityProvider}});
            return { success: true, message: "Forwared to the provider" }
        } catch (error) {
            console.error(`[federatedSignIn(${identityProvider})]`, parseErrorMessage(error), JSON.stringify(error));
            return {
                success: false,
                message: parseErrorMessage(error),
            }
        }
    };

    const forgotPassword = async (email: string, config: AmplifyConfig) => {
        try {
            configureAmplify(config);
            await Auth.resetPassword({username: email.toLowerCase()});
            return { success: true, message: "Forgot password email sent"};
        } catch (error) {
            console.error(`[forgotPassword(${email})]`, parseErrorMessage(error), JSON.stringify(error));
            return {
                success: false,
                message: parseErrorMessage(error),
            }
        }
    };

    const forgotPasswordSubmit = async (email: string, code: string, password: string, config: AmplifyConfig) => {
        try {
            configureAmplify(config);
            await Auth.confirmResetPassword({username: email.toLowerCase(), confirmationCode: code, newPassword: password});
            return { success: true, message: "Password has reset"};
        } catch (error) {
            console.error(`[forgotPasswordSubmit(${email})]`, parseErrorMessage(error), JSON.stringify(error));
            return {
                success: false,
                message: parseErrorMessage(error),
            }
        }
    };

    const confirmVerificationCode = async (email: string, code: string, config: AmplifyConfig) => {
        try {
            configureAmplify(config);
            // NOTE: there is no way to check if the code is valid or not, so call confirmResetPassword() with dummy password
            // reference: https://github.com/aws-amplify/amplify-js/issues/4876
            await Auth.confirmResetPassword({username: email.toLowerCase(), confirmationCode: code, newPassword: "dummy"});
            return { success: true, message: "Password has reset"};
        } catch (error) {
            if ((error as any)?.name === "InvalidPasswordException") {
                return { success: true, message: ""};
            }

            console.error(`[confirmVerificationCode(${email})]`, parseErrorMessage(error), JSON.stringify(error));
            return {
                success: false,
                message: parseErrorMessage(error),
            }
        }
    };

    const fetchAuthSession = async (config: AmplifyConfig) => {
        configureAmplify(config);
        const authSession = await Auth.fetchAuthSession();
        return authSession;
    }

    return {
        isConfigured,
        isSignedIn,
        needMFA,
        configureAmplify,
        signIn: signIn,
        sendMfaCode,
        signOut,
        federatedSignIn,
        forgotPassword,
        forgotPasswordSubmit,
        confirmVerificationCode,
        fetchAuthSession,
    };
};
